import React from "react";
import { Line } from "react-chartjs-2";
import { MetricKpi } from "../../MetricCards";
import panelStyles from "../../styles/Panel.module.css";
import analyticsStyles from "../../styles/Analytics.module.css";
import { PURCHASE_SERIES_COLORS } from "../../constants";
import { formatNumber } from "../../utils/formatters";
import { buildPurchaseSeriesChartData } from "../../utils/chartUtils";

function sumSeries(data = []) {
  return (Array.isArray(data) ? data : []).reduce((acc, value) => acc + Number(value || 0), 0);
}

export function AnalyticsPurchaseSeriesBlock({ purchaseSeries }) {
  const series = Array.isArray(purchaseSeries?.series) ? purchaseSeries.series : [];
  const visibleSeries = series.filter((item) => sumSeries(item.data) > 0);
  const chartData = buildPurchaseSeriesChartData(
    { ...purchaseSeries, series: visibleSeries },
    PURCHASE_SERIES_COLORS
  );
  const hasChartData = chartData.labels.length > 0 && visibleSeries.length > 0;

  const totalPurchases = visibleSeries.reduce((acc, item) => acc + Number(item.total || 0), 0);
  const bestSeries = visibleSeries.reduce((best, item) => {
    const currentTotal = Number(item.total || 0);
    if (!best || currentTotal > Number(best.total || 0)) return item;
    return best;
  }, null);
  const latestDayPurchases = visibleSeries.reduce((acc, item) => {
    const data = Array.isArray(item.data) ? item.data : [];
    return acc + Number(data[data.length - 1] || 0);
  }, 0);
  const peakByDay = chartData.labels.reduce((best, label, index) => {
    const total = visibleSeries.reduce((acc, item) => {
      const data = Array.isArray(item.data) ? item.data : [];
      return acc + Number(data[index] || 0);
    }, 0);
    if (!best || total > best.total) return { label, total };
    return best;
  }, null);

  return (
    <div className={analyticsStyles.block}>
      <div className={analyticsStyles.chartHeader}>
        <div>
          <h3>Покупки по дням за 7 дней</h3>
          <div className={panelStyles.note}>
            Один график показывает динамику по типам покупок: Lite, Pro, Max и разовые покупки.
          </div>
        </div>
      </div>

      {hasChartData ? (
        <>
          <div className={analyticsStyles.chartWrap}>
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                  mode: "index",
                  intersect: false
                },
                plugins: {
                  legend: {
                    position: "top",
                    align: "start",
                    labels: {
                      boxWidth: 10,
                      boxHeight: 10,
                      color: "#D8E2F0",
                      usePointStyle: true,
                      pointStyle: "circle"
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => `${context.dataset.label}: ${formatNumber(context.parsed.y)}`
                    }
                  }
                },
                scales: {
                  x: {
                    ticks: { color: "#9FB2CA" },
                    grid: { color: "rgba(255, 255, 255, 0.06)" }
                  },
                  y: {
                    beginAtZero: true,
                    ticks: {
                      color: "#9FB2CA",
                      precision: 0
                    },
                    grid: { color: "rgba(255, 255, 255, 0.08)" }
                  }
                }
              }}
            />
          </div>

          <div className={analyticsStyles.seriesTotals}>
            {visibleSeries.map((item) => (
              <div key={`purchase-series-${item.key}`} className={analyticsStyles.seriesChip}>
                <span
                  className={analyticsStyles.seriesDot}
                  style={{ backgroundColor: PURCHASE_SERIES_COLORS[item.key] || "#7CE7FF" }}
                />
                <span>{item.label}: {formatNumber(item.total)}</span>
              </div>
            ))}
          </div>

          <div className={analyticsStyles.kpis}>
            <MetricKpi
              title="Покупок за 7 дней"
              value={formatNumber(totalPurchases)}
              hint="Сумма событий purchase по всем типам."
            />
            <MetricKpi
              title="Самый активный тип"
              value={bestSeries ? `${bestSeries.label} (${formatNumber(bestSeries.total)})` : "—"}
              hint="Лидер по количеству покупок за последние 7 дней."
            />
            <MetricKpi
              title="Последний день"
              value={formatNumber(latestDayPurchases)}
              hint="Всего покупок в самой правой точке графика."
            />
            <MetricKpi
              title="Пиковый день"
              value={peakByDay ? `${peakByDay.label} (${formatNumber(peakByDay.total)})` : "—"}
              hint="День с максимальным общим числом покупок."
            />
          </div>
        </>
      ) : (
        <div className={panelStyles.note}>Пока нет данных по покупкам за последние 7 дней.</div>
      )}
    </div>
  );
}

export default AnalyticsPurchaseSeriesBlock;
